/*  ========================================================================
    # Components - Services Illustration
    ========================================================================  */

import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import bem from '../../helpers/bem';
import useTranslation from '../../hooks/useTranslation';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

const bubbleStyle = hover => ({
  transform: `scale(${hover ? 1.07 : 1})`,
  backgroundColor: `rgba(255, 255, 255, ${hover ? 0.4 : 0.3})`
});

const ServicesIllustration = () => {
  const __ = useTranslation();

  // See if the user prefers reduced motion
  const prefersReducedMotion = usePrefersReducedMotion();

  // Store refs for bubbles
  const refCommunications = useRef(null);
  const refInteractive = useRef(null);
  const refPerformance = useRef(null);

  // Store hovers in state variables
  const [hoverGlobal, setHoverGlobal] = useState(false);
  const [hoverCommunications, setHoverCommunications] = useState(false);
  const [hoverInteractive, setHoverInteractive] = useState(false);
  const [hoverPerformance, setHoverPerformance] = useState(false);

  // Check object intersection
  useEffect(() => {
    const checkIntersection = event => {
      if (hoverGlobal) {
        const hoveredElements = document.elementsFromPoint(event.clientX, event.clientY);
        setHoverCommunications(hoveredElements.includes(refCommunications.current));
        setHoverInteractive(hoveredElements.includes(refInteractive.current));
        setHoverPerformance(hoveredElements.includes(refPerformance.current));
      }
    };
    window.addEventListener('mousemove', checkIntersection);
    return () => window.removeEventListener('mousemove', checkIntersection);
  }, [hoverGlobal]);

  // Generate the styles using react-spring
  const springConfig = { mass: 6, tension: 300, friction: 25 };
  const communicationsStyles = useSpring({
    ...bubbleStyle(hoverCommunications),
    config: springConfig,
    immediate: prefersReducedMotion
  });
  const interactiveStyles = useSpring({
    ...bubbleStyle(hoverInteractive),
    config: springConfig,
    immediate: prefersReducedMotion
  });
  const performanceStyles = useSpring({
    ...bubbleStyle(hoverPerformance),
    config: springConfig,
    immediate: prefersReducedMotion
  });

  return (
    <div
      onMouseEnter={() => setHoverGlobal(true)}
      onMouseLeave={() => setHoverGlobal(false)}
      className={bem('c-services-illustration')}
    >
      <div className={bem('c-services-illustration', 'outer')}>
        <div
          className={bem('c-services-illustration', 'label', { strategy: true })}
          dangerouslySetInnerHTML={{ __html: __('services-strategy') }}
        />
        <animated.div
          ref={refCommunications}
          className={bem('c-services-illustration', 'inner', { communications: true })}
          style={communicationsStyles}
        />
        <div
          className={bem('c-services-illustration', 'label', { communications: true })}
          dangerouslySetInnerHTML={{ __html: __('services-communications') }}
        />
        <animated.div
          ref={refInteractive}
          className={bem('c-services-illustration', 'inner', { interactive: true })}
          style={interactiveStyles}
        />
        <div
          className={bem('c-services-illustration', 'label', { interactive: true })}
          dangerouslySetInnerHTML={{ __html: __('services-interactive') }}
        />
        <animated.div
          ref={refPerformance}
          className={bem('c-services-illustration', 'inner', { performance: true })}
          style={performanceStyles}
        />
        <div
          className={bem('c-services-illustration', 'label', { performance: true })}
          dangerouslySetInnerHTML={{ __html: __('services-performance') }}
        />
      </div>
    </div>
  );
};

export default ServicesIllustration;
